import React from 'react';
import { ShieldCheck, Sparkles, ExternalLink, Coins, Eye } from 'lucide-react';
import {
  NEXTXUS_TOKEN_URL,
  NEXTXUS_TOKEN_ALT,
  NEXTXUS_TOKEN_PRICE_USD,
  RING_OF_6_URL,
  RING_OF_6_ALT,
} from '../data/federationAssets';

/**
 * CommerceAndRingStrip — paired surface for the two canonical
 * NextXus product visuals:
 *
 *   • NextXus Token ($5 bearer-instrument) — commerce anchor
 *   • Ring of 6 (Verification / Perspective Layer) — verified-peer anchor
 *
 * Both cards are fully click-link-work per the Marketable Masterpiece
 * standard: Token CTA routes to /redeem; Ring of 6 CTA routes to
 * Truth Verification at nextxus.org.
 */
export default function CommerceAndRingStrip() {
  return (
    <section
      data-testid="commerce-and-ring-strip"
      className="bg-black border-y border-amber-500/15"
    >
      <div className="container mx-auto px-4 py-16 grid md:grid-cols-2 gap-6 lg:gap-10">
        {/* ============ Token card ============ */}
        <article
          data-testid="token-spotlight"
          className="relative overflow-hidden rounded-3xl border-2 border-amber-300/40 bg-gradient-to-br from-amber-950/30 via-gray-900 to-black p-6 lg:p-7"
          style={{ boxShadow: '0 0 32px rgba(251,191,36,0.12)' }}
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-400/12 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            <img
              src={NEXTXUS_TOKEN_URL}
              alt={NEXTXUS_TOKEN_ALT}
              data-testid="token-spotlight-image"
              loading="lazy"
              decoding="async"
              className="flex-shrink-0 w-32 h-32 sm:w-36 sm:h-36 rounded-2xl object-cover border-2 border-amber-300/60"
              style={{ boxShadow: '0 12px 28px rgba(0,0,0,0.6), 0 0 22px rgba(251,191,36,0.25)' }}
            />
            <div className="flex-1 text-center sm:text-left">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-300/85 mb-2 inline-flex items-center gap-1.5">
                <Coins className="w-3 h-3" />
                Official NextXus Token
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight leading-tight">
                ${NEXTXUS_TOKEN_PRICE_USD} <span className="text-amber-300">Digital Credit</span>
              </h3>
              <p className="mt-3 text-sm md:text-base text-gray-300 leading-relaxed">
                The unified bearer-instrument token. Buy one through Gumroad, redeem the code
                here to unlock any token-gated course. Tokens are currency; courses cost
                tokens — that&apos;s the whole rule.
              </p>
              <div className="mt-5 flex flex-wrap justify-center sm:justify-start gap-2">
                <a
                  href="/exchange"
                  data-testid="token-spotlight-redeem"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Spend tokens at the Exchange
                </a>
                <a
                  href="https://nextxus.one"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="token-spotlight-storefront"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
                >
                  Buy at Storefront
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* ============ Ring of 6 card ============ */}
        <article
          data-testid="ring-of-6-card"
          className="relative overflow-hidden rounded-3xl border-2 border-cyan-300/30 bg-gradient-to-br from-cyan-950/30 via-gray-900 to-black p-6 lg:p-7"
          style={{ boxShadow: '0 0 32px rgba(34,211,238,0.12)' }}
        >
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-cyan-400/12 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            <img
              src={RING_OF_6_URL}
              alt={RING_OF_6_ALT}
              data-testid="ring-of-6-image"
              loading="lazy"
              decoding="async"
              className="flex-shrink-0 w-32 h-32 sm:w-36 sm:h-36 rounded-2xl object-cover border-2 border-cyan-300/60"
              style={{ boxShadow: '0 12px 28px rgba(0,0,0,0.6), 0 0 22px rgba(34,211,238,0.25)' }}
            />
            <div className="flex-1 text-center sm:text-left">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-300/85 mb-2 inline-flex items-center gap-1.5">
                <Eye className="w-3 h-3" />
                Ring of 6 · Verification Layer
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight leading-tight">
                Verified <span className="text-cyan-300">Perspective</span>
              </h3>
              <p className="mt-3 text-sm md:text-base text-gray-300 leading-relaxed">
                Six independent perspectives convene to verify a claim. The Ring of 6 is
                Truth Verification&apos;s working layer — subscribe to a Starter Ring to
                practice question-building with a small council of perspectives.
              </p>
              <div className="mt-5 flex flex-wrap justify-center sm:justify-start gap-2">
                <a
                  href="https://nextxus.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ring-of-6-verify"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black font-bold text-sm transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Verify at Truth
                </a>
                <a
                  href="/charter"
                  data-testid="ring-of-6-learn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-300/40 text-cyan-200 hover:bg-cyan-300/10 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
                >
                  Read DIR-000 Truth Gate
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
